import { initializePaddle } from "@paddle/paddle-js";
import axios from "axios";
import { getRefCookie } from "@/lib/referral";

// Build-time fallbacks (used only if the backend config call fails).
const FALLBACK_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN || "";
const FALLBACK_PRICE = process.env.REACT_APP_PADDLE_PRICE_ID || "";
const FALLBACK_ENV = process.env.REACT_APP_PADDLE_ENVIRONMENT || "sandbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Runtime config pulled from the backend. The backend reads live env vars at
// request time, so switching PADDLE_ENVIRONMENT=production on the server
// instantly flips the client overlay to live mode on the next page load — no
// frontend rebuild required.
let runtimeConfig = null;
let runtimeConfigPromise = null;

async function loadRuntimeConfig() {
  if (runtimeConfig) return runtimeConfig;
  if (runtimeConfigPromise) return runtimeConfigPromise;
  runtimeConfigPromise = axios
    .get(`${BACKEND_URL}/api/paddle/config`, { timeout: 6000 })
    .then((res) => {
      const d = res.data || {};
      runtimeConfig = {
        token: d.client_token || FALLBACK_TOKEN,
        priceId: d.price_id || FALLBACK_PRICE,
        environment:
          d.environment === "production" ? "production" : (FALLBACK_ENV === "production" ? "production" : "sandbox"),
        configured: Boolean(d.configured),
      };
      return runtimeConfig;
    })
    .catch(() => {
      runtimeConfig = {
        token: FALLBACK_TOKEN,
        priceId: FALLBACK_PRICE,
        environment: FALLBACK_ENV === "production" ? "production" : "sandbox",
        configured: Boolean(FALLBACK_TOKEN && FALLBACK_PRICE && FALLBACK_TOKEN.length > 10),
      };
      return runtimeConfig;
    });
  return runtimeConfigPromise;
}

let paddleInstance = null;
let paddlePromise = null;

/** True once we've fetched config and both token + price are present. */
export async function isPaddleConfigured() {
  const cfg = await loadRuntimeConfig();
  return Boolean(cfg.token && cfg.priceId && cfg.token.length > 10);
}

export async function getPaddleEnvironment() {
  const cfg = await loadRuntimeConfig();
  return cfg.environment;
}

export async function getPaddlePriceId() {
  const cfg = await loadRuntimeConfig();
  return cfg.priceId;
}

/** Lazy-init and cache the Paddle instance using runtime config. */
export async function getPaddle(eventCallback) {
  const cfg = await loadRuntimeConfig();
  if (!cfg.token || !cfg.priceId) return null;
  if (paddleInstance) return paddleInstance;
  if (paddlePromise) return paddlePromise;

  paddlePromise = initializePaddle({
    environment: cfg.environment, // "production" or "sandbox" (never defaulted)
    token: cfg.token,
    eventCallback,
  }).then((p) => {
    paddleInstance = p;
    return p;
  });
  return paddlePromise;
}

/**
 * Low-level Paddle overlay opener. Both the main-doc checkout and the
 * affiliate enrollment call this so the overlay behaves identically — same
 * instance, same theme, same event wiring. Only the price-id + customer +
 * customData + successUrl differ.
 */
async function openOverlay({ priceId, email, customData, successUrl, onEvent }) {
  const paddle = await getPaddle(onEvent);
  if (!paddle) throw new Error("Paddle is not configured");
  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customer: { email: (email || "").trim() },
    customData: customData || {},
    settings: {
      displayMode: "overlay",
      theme: "light",
      locale: "en",
      successUrl:
        successUrl || `${window.location.origin}${window.location.pathname}?paddle_success=1`,
    },
  });
}

/** Open the Paddle overlay for the $3 Digital Doc purchase. */
export async function openPaddleCheckout({ name, email, phone, locale, onEvent }) {
  const cfg = await loadRuntimeConfig();
  const ref = getRefCookie();
  const safeEmail = (email || "").trim();
  await openOverlay({
    priceId: cfg.priceId,
    email: safeEmail,
    customData: {
      // Mirror the email in customData so the webhook can resolve the buyer
      // even when Paddle's live transaction payloads omit inline customer.email.
      email: safeEmail,
      name: (name || "").trim().slice(0, 200),
      phone: (phone || "").trim().slice(0, 50),
      locale: locale || "en",
      ...(ref && ref.affiliate_id
        ? { ref_code: ref.affiliate_id, ref_source: ref.source || "link" }
        : {}),
    },
    onEvent,
  });
}

/**
 * Open the Paddle overlay for the $2.99 affiliate-enrollment purchase.
 * Uses the exact same overlay plumbing as the Digital Doc checkout — just
 * with a different price-id and the enrollment customData so our webhook
 * can route it correctly.
 */
export async function openPaddleEnrollment({ priceId, email, affiliateId, userId, onEvent }) {
  if (!priceId) throw new Error("Enrollment price id is required");
  const safeEmail = (email || "").trim();
  await openOverlay({
    priceId,
    email: safeEmail,
    customData: {
      email: safeEmail,  // Mirror the email — see openPaddleCheckout for rationale
      purpose: "affiliate_enrollment",
      affiliate_id: affiliateId,
      user_id: userId,
    },
    successUrl: `${window.location.origin}/affiliate/dashboard?enrollment=success`,
    onEvent,
  });
}
